import { Link } from '@tanstack/react-router'
import { Snowflake } from 'lucide-react'
import { useState } from 'react'

import type { BuildReportSnapshotRequest, ReportDimension, ReportSnapshotType } from '@/api/reports-types'
import { ErrorSurface } from '@/components/error-surface'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatDateTime } from '@/lib/datetime'
import { resolveErrorMessage } from '@/lib/error-messages'

import { DIMENSION_LABEL, REPORT_DIMENSIONS, SNAPSHOT_STATUS_TONE, SNAPSHOT_TYPE_LABEL, resolveBranchLabel, today } from './constants'
import { BranchFilterField, LoanOfficerFilterField } from './filters'
import { REPORTS_PERMISSIONS, useReportsPermissions } from './permissions'
import { useBuildReportSnapshot, useReportSnapshots, useReportsBranchReach } from './queries'
import { RestrictedPanel } from './restricted-panel'

const SNAPSHOT_TYPES: ReportSnapshotType[] = [
  'PortfolioAtRisk',
  'OfficerPerformance',
  'TrialBalance',
  'GroupHealth',
  'CollectionsRegister',
  'DisbursementsRegister',
]

const RANGE_TYPES = new Set<ReportSnapshotType>(['CollectionsRegister', 'DisbursementsRegister'])
const DIMENSIONED_TYPES = new Set<ReportSnapshotType>(['PortfolioAtRisk', 'OfficerPerformance'])

/**
 * Freezing a report as at a date, and browsing what has already been frozen.
 *
 * A snapshot is what a figure a board or a regulator was quoted rests on: the live screens re-read
 * the book on every request, and a late-synced collection or a backdated correction will move them
 * between two runs for the same as-of date. A snapshot answers what was reported, not what the book
 * currently says about that date — see `StoredReport`'s own note on the payload hash that proves it
 * has not moved since.
 *
 * Building one needs the permission the report itself needs — `reports.par.view` to freeze a
 * portfolio-at-risk snapshot, `reports.trialbalance.view` for a trial balance — checked by the
 * server against the report type named in the form, not by this screen. The list below is gated
 * more loosely, on the module's own broad reach, because the server itself filters it to the report
 * types the caller may see rather than refusing the whole list for a narrow grant.
 */
export function SnapshotsSection() {
  const permissions = useReportsPermissions()

  if (!permissions.canBrowseSnapshots) {
    return <RestrictedPanel what="Report snapshots" permission={REPORTS_PERMISSIONS.trialBalanceView} />
  }

  return (
    <div className="space-y-4">
      <BuildSnapshotCard />
      <SnapshotListCard />
    </div>
  )
}

function BuildSnapshotCard() {
  const mutation = useBuildReportSnapshot()

  const [reportType, setReportType] = useState<ReportSnapshotType>('PortfolioAtRisk')
  const [asOf, setAsOf] = useState(today)
  const [fromDate, setFromDate] = useState(() => today())
  const [toDate, setToDate] = useState(today)
  const [dimension, setDimension] = useState<ReportDimension>('Branch')
  const [branchId, setBranchId] = useState<string | null>(null)
  const [loanOfficerId, setLoanOfficerId] = useState<string | null>(null)
  const [rebuild, setRebuild] = useState(false)

  const isRange = RANGE_TYPES.has(reportType)
  const isDimensioned = DIMENSIONED_TYPES.has(reportType)

  const request: BuildReportSnapshotRequest = {
    reportType,
    asOf: isRange ? null : asOf,
    fromDate: isRange ? fromDate : null,
    toDate: isRange ? toDate : null,
    dimension: isDimensioned ? dimension : null,
    branchId: branchId === null ? null : Number(branchId),
    loanOfficerId,
    groupId: null,
    rebuild,
  }

  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle className="flex items-center gap-2">
            <Snowflake className="size-4 text-muted" aria-hidden />
            Freeze a report
          </CardTitle>
          <p className="mt-0.5 text-xs text-muted">
            Stores the figures as at this moment. A second build for the same report, date, period
            and scope is refused unless you tick rebuild, which supersedes the first and keeps both.
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="snap-type">Report</Label>
            <Select value={reportType} onValueChange={(next) => setReportType(next as ReportSnapshotType)}>
              <SelectTrigger id="snap-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SNAPSHOT_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {SNAPSHOT_TYPE_LABEL[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isRange ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="snap-from">From</Label>
                <Input id="snap-from" type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="snap-to">To</Label>
                <Input id="snap-to" type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
              </div>
            </>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="snap-as-of">As of</Label>
              <Input id="snap-as-of" type="date" value={asOf} onChange={(event) => setAsOf(event.target.value)} />
            </div>
          )}

          {isDimensioned ? (
            <div className="space-y-1.5">
              <Label htmlFor="snap-dimension">Dimension</Label>
              <Select value={dimension} onValueChange={(next) => setDimension(next as ReportDimension)}>
                <SelectTrigger id="snap-dimension">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_DIMENSIONS.map((candidate) => (
                    <SelectItem key={candidate} value={candidate}>
                      {DIMENSION_LABEL[candidate]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <BranchFilterField id="snap-branch" branchId={branchId} onChange={setBranchId} />
          <LoanOfficerFilterField id="snap-officer" branchId={branchId} loanOfficerId={loanOfficerId} onChange={setLoanOfficerId} />
        </div>

        <label className="flex items-center gap-2 text-sm text-body max-lg:min-h-11">
          <Checkbox checked={rebuild} onCheckedChange={(next) => setRebuild(next === true)} />
          Rebuild — supersede an existing snapshot for this report, date, period and scope
        </label>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="primary" disabled={mutation.isPending} onClick={() => mutation.mutate(request)}>
            {mutation.isPending ? 'Freezing…' : 'Freeze this report'}
          </Button>
          {mutation.isError ? (
            <span className="text-xs text-danger-text">{resolveErrorMessage(mutation.error)}</span>
          ) : null}
        </div>

        {mutation.isSuccess ? (
          <Alert tone="success">
            <Snowflake aria-hidden />
            <div className="min-w-0 space-y-1">
              <AlertTitle>Frozen</AlertTitle>
              <AlertDescription>
                <Link
                  to="/reports/snapshots/$snapshotId"
                  params={{ snapshotId: mutation.data.snapshotId }}
                  className="font-medium text-brand-text hover:text-brand-text-hover"
                >
                  Open this snapshot
                </Link>
                {' — '}
                {mutation.data.status === 'Complete'
                  ? 'every source answered.'
                  : `status: ${mutation.data.status}. Not every source answered — see the snapshot for which.`}
              </AlertDescription>
            </div>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  )
}

function SnapshotListCard() {
  const reach = useReportsBranchReach()
  const [page, setPage] = useState(1)
  const pageSize = 20
  const query = useReportSnapshots({ page, pageSize })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Frozen snapshots</CardTitle>
        {query.data ? <Badge tone="neutral">{query.data.totalRows} total</Badge> : null}
      </CardHeader>
      <CardContent className="p-0">
        {query.isPending ? (
          <div className="p-4">
            <Skeleton className="h-48 w-full" />
          </div>
        ) : query.isError ? (
          <div className="p-4">
            <ErrorSurface error={query.error} onRetry={() => void query.refetch()} title="Snapshots could not be listed" />
          </div>
        ) : query.data.rows.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-body">
            Nothing has been frozen yet — or none of what has been is visible to your role.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <tr>
                    <TableHead scope="col">Report</TableHead>
                    <TableHead scope="col">Status</TableHead>
                    <TableHead scope="col">As of / period</TableHead>
                    <TableHead scope="col">Branch</TableHead>
                    <TableHead scope="col" className="text-right">Rows</TableHead>
                    <TableHead scope="col">Generated</TableHead>
                    <TableHead scope="col" />
                  </tr>
                </TableHeader>
                <TableBody>
                  {query.data.rows.map((row) => (
                    <TableRow key={row.snapshotId}>
                      <TableCell className="text-sm font-medium text-heading">
                        {SNAPSHOT_TYPE_LABEL[row.reportType]}
                      </TableCell>
                      <TableCell>
                        <Badge tone={SNAPSHOT_STATUS_TONE[row.status]}>{row.status}</Badge>
                        {row.supersededBy !== null ? (
                          <Badge tone="neutral" className="ml-1">Superseded</Badge>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-xs">
                        {row.fromDate !== null && row.toDate !== null ? `${row.fromDate} – ${row.toDate}` : row.asOf}
                      </TableCell>
                      <TableCell className="text-xs">
                        {row.branchId === null ? 'Whole institution' : resolveBranchLabel(reach.branches, row.branchId)}
                      </TableCell>
                      <TableCell className="numeric text-right tabular-nums">{row.rowCount}</TableCell>
                      <TableCell className="text-2xs text-muted">
                        <span className="numeric tabular-nums">{formatDateTime(row.generatedAtUtc)}</span>
                      </TableCell>
                      <TableCell>
                        <Link
                          to="/reports/snapshots/$snapshotId"
                          params={{ snapshotId: row.snapshotId }}
                          className="text-xs font-medium text-brand-text hover:text-brand-text-hover"
                        >
                          Open
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-border-subtle p-3">
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </Button>
              <span className="text-2xs text-muted">
                Page {query.data.page} of {Math.max(query.data.totalPages, 1)}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= query.data.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
